/**
 * Image Compression Utility for AxisIndia VerifyPro
 * Compresses images before upload to prevent timeouts while maintaining quality.
 * Automatically corrects EXIF orientation (mobile camera photos) and enforces
 * portrait orientation (rotates landscape images 90° CW to portrait).
 */

const IMAGE_MAX_WIDTH = 1920;
const IMAGE_MAX_HEIGHT = 1080;
const DEFAULT_QUALITY = 0.85;
const DEFAULT_MAX_SIZE_KB = 500;
const KB_DIVISOR = 1024;

// ─── EXIF orientation helpers ─────────────────────────────────────────────────

/**
 * Parse EXIF from a JPEG ArrayBuffer.
 * Returns { make: string, orientation: number }.
 * make is e.g. "Apple" for iPhone, "" if not found.
 * orientation: 1=normal, 3=180°, 6=90°CW, 8=90°CCW
 */
function readExifMeta(buffer) {
  const result = { make: '', orientation: 1 };
  try {
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) return result;
    let offset = 2;
    while (offset + 4 < view.byteLength) {
      const marker = view.getUint16(offset, false);
      const segLen  = view.getUint16(offset + 2, false);
      if (marker === 0xFFE1) {
        if (
          offset + 10 < view.byteLength &&
          view.getUint32(offset + 4, false) === 0x45786966 &&
          view.getUint16(offset + 8, false) === 0x0000
        ) {
          const tiffBase = offset + 10;
          const little   = view.getUint16(tiffBase, false) === 0x4949;
          const ifd0     = tiffBase + view.getUint32(tiffBase + 4, little);
          const entries  = view.getUint16(ifd0, little);
          const decoder  = new TextDecoder('ascii');
          for (let i = 0; i < entries; i++) {
            const e = ifd0 + 2 + i * 12;
            if (e + 12 > view.byteLength) break;
            const tag = view.getUint16(e, little);
            if (tag === 0x010F) {
              // Make — ASCII string, count at e+4, value/offset at e+8
              const count     = view.getUint32(e + 4, little);
              const valOffset = view.getUint32(e + 8, little);
              const start     = count <= 4 ? e + 8 : tiffBase + valOffset;
              if (start + count <= view.byteLength) {
                result.make = decoder.decode(new Uint8Array(buffer, start, count)).replace(/\0/g, '').trim();
              }
            } else if (tag === 0x0112) {
              result.orientation = view.getUint16(e + 8, little);
            }
          }
        }
      }
      if ((marker & 0xFF00) !== 0xFF00) break;
      offset += 2 + segLen;
    }
  } catch { /* ignore — non-JPEG or truncated EXIF */ }
  return result;
}

/**
 * Draw `img` onto `canvas` with the EXIF orientation corrected.
 * Canvas must already have the correct (post-correction) dimensions set:
 *   - orientations 1–4: (naturalWidth × naturalHeight)
 *   - orientations 5–8: (naturalHeight × naturalWidth)  ← swapped
 */
function drawWithOrientation(canvas, img, orientation) {
  const ctx = canvas.getContext('2d');
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  ctx.save();
  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0,  1,  w,  0); break; // flip H
    case 3: ctx.transform(-1, 0, 0, -1,  w,  h); break; // 180°
    case 4: ctx.transform( 1, 0, 0, -1,  0,  h); break; // flip V
    case 5: ctx.transform( 0, 1, 1,  0,  0,  0); break; // transpose
    case 6: ctx.transform( 0, 1,-1,  0,  h,  0); break; // 90° CW
    case 7: ctx.transform( 0,-1,-1,  0,  h,  w); break; // transverse
    case 8: ctx.transform( 0,-1, 1,  0,  0,  w); break; // 90° CCW
    default: break;
  }
  ctx.drawImage(img, 0, 0);
  ctx.restore();
}

/**
 * If canvas is landscape (width > height), rotate it 90° CW to portrait.
 * Returns the same canvas if already portrait, or a new rotated one.
 */
function enforcePortrait(canvas) {
  if (canvas.width <= canvas.height) return canvas;
  const rotated = document.createElement('canvas');
  rotated.width  = canvas.height;
  rotated.height = canvas.width;
  const ctx = rotated.getContext('2d');
  ctx.translate(rotated.width / 2, rotated.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  return rotated;
}

// ─── Scale helpers ────────────────────────────────────────────────────────────

function scaleDimensions(width, height, maxWidth, maxHeight) {
  let w = width, h = height;
  if (w > maxWidth)  { h = (h * maxWidth)  / w; w = maxWidth; }
  if (h > maxHeight) { w = (w * maxHeight) / h; h = maxHeight; }
  return { width: Math.round(w), height: Math.round(h) };
}

function compressCanvas(canvas, quality, maxSizeKB) {
  const maxBytes = maxSizeKB * KB_DIVISOR;
  let q = quality;
  let dataUrl = canvas.toDataURL('image/jpeg', q);
  let attempts = 0;
  while (dataUrl.length * 0.75 > maxBytes && q > 0.3 && attempts < 5) {
    q -= 0.1;
    dataUrl = canvas.toDataURL('image/jpeg', q);
    attempts++;
  }
  return dataUrl.split(',')[1];
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Compress an image File.
 * - Reads EXIF orientation and corrects it on canvas.
 * - Rotates landscape images to portrait (90° CW).
 * - Scales to maxWidth/maxHeight and compresses to maxSizeKB.
 * Returns a Promise<string> of base64 without the data-URL prefix.
 */
export const compressImage = (file, options = {}) => {
  const {
    maxWidth  = IMAGE_MAX_WIDTH,
    maxHeight = IMAGE_MAX_HEIGHT,
    quality   = DEFAULT_QUALITY,
    maxSizeKB = DEFAULT_MAX_SIZE_KB,
  } = options;

  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    // Read EXIF to detect iPhone (Make = "Apple") before loading the image.
    // iPhone camera photos arrive 180° rotated; Android photos are already correct.
    file.arrayBuffer().then((buffer) => {
      const { make } = readExifMeta(buffer);
      const isIphone = make.toLowerCase().includes('apple');
      const blobUrl  = URL.createObjectURL(file);

      const img = new Image();
      img.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error('Failed to load image')); };

      img.onload = () => {
        URL.revokeObjectURL(blobUrl);
        const { width, height } = scaleDimensions(img.naturalWidth, img.naturalHeight, maxWidth, maxHeight);
        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        if (isIphone) {
          // Rotate 180° to correct iPhone camera upload orientation
          ctx.translate(width / 2, height / 2);
          ctx.rotate(Math.PI);
          ctx.drawImage(img, -width / 2, -height / 2, width, height);
        } else {
          ctx.drawImage(img, 0, 0, width, height);
        }

        resolve(compressCanvas(canvas, quality, maxSizeKB));
      };

      img.src = blobUrl;
    }).catch(() => {
      // Fallback when arrayBuffer() is unavailable — draw as-is
      const blobUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error('Failed to load image')); };
      img.onload = () => {
        URL.revokeObjectURL(blobUrl);
        const { width, height } = scaleDimensions(img.naturalWidth, img.naturalHeight, maxWidth, maxHeight);
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        resolve(compressCanvas(canvas, quality, maxSizeKB));
      };
      img.src = blobUrl;
    });
  });
};

/**
 * Compress image from a base64 string.
 * Applies portrait enforcement but no EXIF correction (no raw file bytes available).
 */
export const compressBase64Image = (base64, options = {}) => {
  const {
    maxWidth  = IMAGE_MAX_WIDTH,
    maxHeight = IMAGE_MAX_HEIGHT,
    quality   = DEFAULT_QUALITY,
    maxSizeKB = DEFAULT_MAX_SIZE_KB,
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('Failed to load image'));
    img.onload = () => {
      const { width, height } = scaleDimensions(img.width, img.height, maxWidth, maxHeight);
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      resolve(compressCanvas(enforcePortrait(canvas), quality, maxSizeKB));
    };
    img.src = base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
  });
};

/**
 * Compress image from canvas/video capture.
 * Applies portrait enforcement (no EXIF — camera stream is already oriented).
 */
export const compressFromCanvas = (source, options = {}) => {
  const {
    maxWidth  = IMAGE_MAX_WIDTH,
    maxHeight = IMAGE_MAX_HEIGHT,
    quality   = DEFAULT_QUALITY,
  } = options;

  let srcW = source instanceof HTMLVideoElement ? source.videoWidth  : source.width;
  let srcH = source instanceof HTMLVideoElement ? source.videoHeight : source.height;

  const { width, height } = scaleDimensions(srcW, srcH, maxWidth, maxHeight);

  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);

  const final = enforcePortrait(canvas);
  return final.toDataURL('image/jpeg', quality).split(',')[1];
};

export default compressImage;
